import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";

import { CarrierStatsRow } from "components/CarrierStatsRow";
import { CompanyLogoTile } from "components/CompanyLogoTile";
import { buildCarrierProfileUrl } from "components/CompanyProfile/utils";
import { CarrierProfileSource, EVOTRUX_BLUE } from "constants/index";
import { useCarrierStats, useShipperShipmentPageContext } from "hooks";

import {
  AddCarrierToNetworkButton,
  FollowShipperRequestSource,
} from "../../AddCarrierToNetworkButton";

import type { AddCarrierAnnouncement } from "../../AddCarrierToNetworkButton";

interface Props {
  quote: ShipperCondensedQuote;
  sentFollowRequests: FollowShipperRequestAndResponse[] | null;
  carrierJustInvited: boolean;
  onAddPartnerStarted: (carrierId: number) => void;
  onAddPartnerOutcome: (
    carrierId: number,
    announcement: AddCarrierAnnouncement,
  ) => void;
}

const LOGO_TILE_SIZE_PX = 72;

const BUSINESS_TYPE_LABEL: Record<string, string> = {
  BROKER: "Freight broker",
  TRUCKING_COMPANY: "Trucking company",
};

/** The person who sent the quote. */
function getSubmitterName(quote: ShipperCondensedQuote): string {
  const name =
    `${quote.created_by?.first_name ?? ""} ${quote.created_by?.last_name ?? ""}`.trim();
  return name || "-";
}

export function QuoteCarrierColumn(props: Props) {
  const {
    quote,
    sentFollowRequests,
    carrierJustInvited,
    onAddPartnerStarted,
    onAddPartnerOutcome,
  } = props;

  const companyId = quote.created_by_company?.id;
  const { carrierMeta } = useCarrierStats({ companyId: companyId ?? null });
  const { logoByCompanyId } = useShipperShipmentPageContext();

  const companyName = quote.created_by_company?.name || "-";
  const logoDownloadUrl = companyId
    ? (logoByCompanyId.get(companyId)?.download_url ?? null)
    : null;

  // The B / T badges are gone: business type is a fact about the company, so it
  // belongs to the name rather than to a glyph beside it.
  const businessTypeLabel =
    BUSINESS_TYPE_LABEL[quote.created_by_company?.business_type ?? ""] ?? null;

  const handleCompanyClick = () => {
    if (companyId) {
      window.open(
        buildCarrierProfileUrl(
          companyId,
          CarrierProfileSource.SHIPMENT_QUOTE_TABLE_ROW,
        ),
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <CompanyLogoTile
        companyName={companyName}
        logoDownloadUrl={logoDownloadUrl}
        size={LOGO_TILE_SIZE_PX}
      />
      <Stack direction="column" spacing={0.25} sx={{ minWidth: 0 }}>
        {/* Row 1 — the company. */}
        <Tooltip title={businessTypeLabel ?? ""}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            onClick={handleCompanyClick}
            sx={{
              cursor: companyId ? "pointer" : "default",
              width: "fit-content",
              "&:hover": {
                "& .carrier-company-name, & .carrier-link-icon": {
                  color: EVOTRUX_BLUE,
                },
              },
            }}
          >
            <Typography
              className="carrier-company-name"
              variant="h6"
              fontWeight={600}
              sx={{ whiteSpace: "nowrap" }}
            >
              {companyName}
            </Typography>
            {companyId && (
              <OpenInNewIcon className="carrier-link-icon" fontSize="small" />
            )}
          </Stack>
        </Tooltip>

        {/* Row 2 — track record. */}
        {carrierMeta && (
          <CarrierStatsRow
            carrierMeta={carrierMeta}
            withCounts
            withTrackingLinkSubmissionRate
            iconSize="small"
            textVariant="body1"
          />
        )}

        {/* Row 3 — the person who sent it. */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 260,
          }}
        >
          {getSubmitterName(quote)}
        </Typography>

        {/* Row 4 — the relationship. Held back until the pending invites are
        known, so a partner's pill does not appear after an empty gap. */}
        {sentFollowRequests != null && (
          <Box sx={{ display: "flex", alignItems: "center", pt: 0.25 }}>
            <AddCarrierToNetworkButton
              carrierId={quote.carrier_id}
              source={FollowShipperRequestSource.SHIPMENT_QUOTE_TABLE_ROW}
              display="link"
              isPartner={quote.is_partner_quote && !carrierJustInvited}
              sentRequests={sentFollowRequests}
              inviteAlreadySent={carrierJustInvited}
              analyticsProperties={{
                quote_id: quote.quote,
                shipment_id: quote.shipment,
              }}
              onSubmitStarted={() => onAddPartnerStarted(quote.carrier_id)}
              onOutcome={(announcement) =>
                onAddPartnerOutcome(quote.carrier_id, announcement)
              }
            />
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
